/**
 * Entrar y salir. Habla directamente con better-auth 1.6.26, que `apps/api`
 * monta en `/api/auth/*` (`basePath` en `apps/api/src/auth.ts:89`).
 *
 * CÓMO SE AVERIGUARON ESTAS RUTAS — no se adivinaron. Se leyó el paquete
 * instalado, `better-auth@1.6.26` en `node_modules`:
 *
 *   - `dist/api/routes/sign-in.mjs:107`
 *     `createAuthEndpoint("/sign-in/social", { method: "POST", body: … })`.
 *     El cuerpo admite `provider`, `callbackURL`, `errorCallbackURL`,
 *     `newUserCallbackURL` y `disableRedirect`. Responde 200 con
 *     `{ url, redirect }`, donde `url` es la URL de autorización de Google.
 *
 *   - `dist/api/routes/sign-out.mjs:4`
 *     `createAuthEndpoint("/sign-out", { method: "POST" })`. Borra la sesión de
 *     la base y la cookie, y responde `{ success: true }`.
 *
 *   - `dist/client/fetch-plugins.mjs` — el `redirectPlugin` del cliente oficial
 *     hace exactamente esto: si la respuesta trae `url` y `redirect`, asigna
 *     `window.location.href = url`. Aquí se hace lo mismo a mano.
 *
 * POR QUÉ NO SE USA `better-auth/react`. La librería solo está instalada como
 * dependencia de `apps/api`; agregarla al frontend obligaría a instalar
 * paquetes. Lo que el frontend necesita son dos POST y una navegación, así que
 * se llaman por HTTP. Si algún día entra el cliente oficial, sustituye a este
 * archivo y a nada más.
 *
 * `disableRedirect: true` es deliberado: sin él, la respuesta trae además un
 * encabezado `Location` y la navegación deja de estar en manos de este código,
 * con lo que un fallo del intercambio no se podría mostrar en pantalla.
 */

import {
  ErrorApi,
  ErrorDeFormato,
  describirFalloDeRed,
  esRegistro,
  leerJson,
  mensajeDelCuerpo,
} from './errores'

const TIEMPO_LIMITE_MS = 10000

/**
 * A dónde vuelve el navegador cuando Google termina, salga bien o mal.
 *
 * Es una ruta relativa a propósito. better-auth la reenvía tal cual en el
 * `Location` del callback, y el navegador la resuelve contra el origen por el
 * que entró — que es el mismo que sirve la aplicación. Una URL absoluta armada
 * aquí se desincronizaría de `BASE_URL`, y el ADR-0006 advierte justo de eso.
 *
 * `/mi-cuenta` y no `/`: es la pantalla que sabe explicar los cuatro desenlaces
 * —sesión lista, falta registro, no hay sesión, o el inicio falló—.
 */
export const RUTA_DE_REGRESO = '/mi-cuenta'

/**
 * Pide la URL de autorización de Google y navega hacia ella.
 *
 * No regresa cuando funciona: la pestaña se va a Google. Lanza si el servidor
 * no puede iniciar el flujo, incluido el 503 de «autenticación no configurada».
 */
export async function entrarConGoogle(): Promise<void> {
  let respuesta: Response
  try {
    respuesta = await fetch('/api/auth/sign-in/social', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        provider: 'google',
        callbackURL: RUTA_DE_REGRESO,
        errorCallbackURL: RUTA_DE_REGRESO,
        disableRedirect: true,
      }),
      signal: AbortSignal.timeout(TIEMPO_LIMITE_MS),
    })
  } catch (error) {
    throw new Error(
      `No se pudo iniciar sesión (${describirFalloDeRed(error)}).`,
    )
  }

  const cuerpo = await leerJson(respuesta)

  if (!respuesta.ok) {
    throw new ErrorApi(
      respuesta.status,
      mensajeDelCuerpo(cuerpo) ??
        `El servidor respondió ${respuesta.status} al iniciar sesión.`,
    )
  }

  const url = esRegistro(cuerpo) ? cuerpo['url'] : undefined

  if (typeof url !== 'string' || url === '') {
    throw new ErrorDeFormato(
      'El servidor aceptó el inicio de sesión pero no devolvió la dirección de Google.',
    )
  }

  window.location.assign(url)
}

/** Cierra la sesión: borra la fila de sesión y la cookie. */
export async function cerrarSesion(): Promise<void> {
  let respuesta: Response
  try {
    respuesta = await fetch('/api/auth/sign-out', {
      method: 'POST',
      headers: { accept: 'application/json' },
      credentials: 'same-origin',
      signal: AbortSignal.timeout(TIEMPO_LIMITE_MS),
    })
  } catch (error) {
    throw new Error(
      `No se pudo cerrar la sesión (${describirFalloDeRed(error)}). ` +
        'Tu sesión sigue abierta.',
    )
  }

  if (!respuesta.ok) {
    const cuerpo = await leerJson(respuesta)
    throw new ErrorApi(
      respuesta.status,
      mensajeDelCuerpo(cuerpo) ??
        `El servidor respondió ${respuesta.status} al cerrar la sesión.`,
    )
  }
}
